import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search } from "lucide-react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { API_BASE } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BookOpen, FolderOpen, Download, ExternalLink, FileText, Presentation, Video, Link as LinkIcon } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { YEAR_LABEL, courseYears, yearsLabel } from "@/lib/school";

const KIND_ICON = { slide: Presentation, pdf: FileText, video: Video, link: LinkIcon, altro: FileText };
const KIND_LABEL = { slide: "Slide", pdf: "PDF", video: "Video", link: "Link", altro: "Altro" };

export default function PortalMaterials() {
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const [subject, setSubject] = useState("all");
  const [q, setQ] = useState("");

  const { data: courses = [], isLoading } = useQuery({
    queryKey: ["portal-courses-me"],
    queryFn: async () => (await api.get("/courses")).data,
  });

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1200px] mx-auto">
      <PageHeader
        title="Materiale didattico"
        subtitle="Slide, dispense, video e link condivisi dai docenti dei tuoi corsi."
      />

      {isLoading ? (
        <div className="text-sm text-stone-400 text-center py-8">Caricamento…</div>
      ) : courses.length === 0 ? (
        <Card className="p-10 text-center text-sm text-stone-400 border border-stone-200 rounded-xl shadow-none">
          Nessun corso disponibile per il tuo anno/percorso.
        </Card>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row gap-2 mb-5" data-testid="materials-filters">
            <Select value={subject} onValueChange={setSubject}>
              <SelectTrigger className="sm:w-80 bg-white" data-testid="materials-subject-select"><SelectValue placeholder="Materia"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le materie</SelectItem>
                {[...courses].sort((a, b) => a.name.localeCompare(b.name)).map((c) => <SelectItem key={c.id} value={c.id} data-testid={`materials-subject-${c.id}`}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca titolo o data (es. 12 ott)" className="pl-9 bg-white" data-testid="materials-search-input"/>
            </div>
          </div>
          <div className="space-y-4">
            {courses.filter((c) => subject === "all" || c.id === subject).map((c) => (
              <CourseMaterialsBlock key={c.id} course={c} token={token} q={q.trim().toLowerCase()} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const matches = (m, q) => !q || [m.title, m.description, fmtDate(m.uploaded_at)].some((v) => (v || "").toLowerCase().includes(q));

function CourseMaterialsBlock({ course, token, q }) {
  const { data: all = [] } = useQuery({
    queryKey: ["portal-materials", course.id],
    queryFn: async () => (await api.get(`/courses/${course.id}/materials`)).data,
  });
  const materials = all.filter((m) => matches(m, q)).sort((a, b) => (b.uploaded_at || "").localeCompare(a.uploaded_at || ""));
  if (q && materials.length === 0) return null;

  return (
    <Card className="border border-stone-200 rounded-xl shadow-none overflow-hidden">
      <div className="p-4 border-b border-stone-100 bg-[#FDFBF7] flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-1 h-6 rounded-full" style={{ background: course.area_color || "#1C352D" }}/>
            <div className="font-medium text-[#1C352D]">{course.name}</div>
            {courseYears(course).length > 0 && <Badge className="bg-[#1C352D] text-white text-[10px] rounded-full">{yearsLabel(course)}</Badge>}
          </div>
          <div className="text-[11px] text-stone-500 mt-1">
            {course.area_name && <span>{course.area_name} · </span>}
            {course.teachers?.map((t) => t.name).join(", ") || "Nessun docente assegnato"}
          </div>
        </div>
        <Badge className="bg-white text-stone-700 border border-stone-200 rounded-full text-[10px]">
          {materials.length} {materials.length === 1 ? "elemento" : "elementi"}
        </Badge>
      </div>
      {materials.length === 0 ? (
        <div className="p-6 text-xs text-stone-400 text-center">Nessun materiale caricato per questo corso.</div>
      ) : (
        <ul className="divide-y divide-stone-100">
          {materials.map((m) => {
            const Icon = KIND_ICON[m.kind] || FileText;
            const isLink = m.kind === "link" || m.url;
            const href = isLink ? m.url : `${API_BASE}/courses/materials/${m.id}/download?auth=${token}`;
            return (
              <li key={m.id} data-testid={`portal-material-${m.id}`}>
                <a
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  className="p-3 flex items-center gap-3 hover:bg-stone-50/60 transition-colors"
                >
                  <div className="w-14 shrink-0 text-center rounded-lg bg-[#1C352D]/5 py-1" data-testid={`portal-material-date-${m.id}`}>
                    <div className="text-lg font-semibold leading-none text-[#1C352D]">{m.uploaded_at ? new Date(m.uploaded_at).getDate() : "—"}</div>
                    <div className="text-[10px] uppercase tracking-wide text-stone-500 mt-0.5">{m.uploaded_at ? new Date(m.uploaded_at).toLocaleDateString("it-IT", { month: "short", year: "2-digit" }) : ""}</div>
                  </div>
                  <div className="w-8 h-8 rounded-lg bg-stone-100 grid place-items-center text-[#1C352D] shrink-0">
                    <Icon size={14}/>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{m.title}</div>
                    <div className="text-[11px] text-stone-500 flex items-center gap-2 flex-wrap mt-0.5">
                      <span>{KIND_LABEL[m.kind] || m.kind}</span>
                      <span>· caricato il {fmtDate(m.uploaded_at)}</span>
                      {m.size > 0 && <span>· {(m.size / 1024 / 1024).toFixed(1)} MB</span>}
                    </div>
                    {m.description && <div className="text-[11px] text-stone-600 mt-1">{m.description}</div>}
                  </div>
                  {isLink ? <ExternalLink size={15} className="text-stone-400"/> : <Download size={15} className="text-stone-400"/>}
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
